/**
 * A página que o profissional divulga — a única tela sem login do produto.
 *
 * Três decisões que valem explicação:
 *
 * **O registro profissional aparece com ressalva.** O Movhuman não confere
 * número de conselho, e mostrar o número sem dizer isso seria emprestar
 * credibilidade que ninguém verificou. O texto diz que a informação é do
 * profissional e que quem quiser confirmar consulta o conselho.
 *
 * **Endereço inexistente e perfil fora do ar são a mesma tela.** A API devolve
 * 404 para os dois — e para o profissional suspenso também. Distinguir contaria
 * a quem sorteia endereços quem tem conta aqui.
 *
 * **Não há botão para virar aluno.** É limite conhecido do Marco 1: quem se
 * interessa entra em contato por fora, e o vínculo começa por um convite que o
 * profissional envia.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { Brand } from '@/components/Brand';
import { getPublicProfessional } from '@/features/profiles/api';
import { ApiError } from '@/lib/apiClient';

export default function PublicProfessionalPage() {
  const { t } = useTranslation(['profiles', 'common']);
  const { slug = '' } = useParams();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['public-professional', slug],
    queryFn: () => getPublicProfessional(slug),
    retry: false,
    enabled: slug.length > 0,
  });

  return (
    <main className="mh-shell px-5 py-10">
      <Brand />

      {isPending ? (
        <p className="text-(--color-sand-700)" aria-busy="true">
          {t('common:state.loading')}
        </p>
      ) : null}

      {/* 404 e "deu ruim" não são a mesma coisa para quem chega.

          Dizer "esta página não existe" quando a API está fora faria o
          profissional achar que a página dele sumiu, e o visitante desistir de
          um endereço que funciona. Só o 404 — que a API devolve para endereço
          inexistente, rascunho e suspenso — merece a mensagem de ausência. */}
      {isError ? (
        <Alert tone="error" live="assertive">
          {error instanceof ApiError && error.status === 404
            ? t('profiles:public.notFound')
            : t('profiles:public.unavailable')}
        </Alert>
      ) : null}

      {data ? (
        <article className="flex flex-col gap-6">
          <header>
            <h1 className="text-3xl">{data.display_name}</h1>
            {data.city ? <p className="mt-1 text-(--color-sand-700)">{data.city}</p> : null}
          </header>

          {data.bio ? (
            // `whitespace-pre-line` preserva as quebras que a pessoa escreveu.
            // Sem isso, uma apresentação com parágrafos vira um bloco só.
            <p className="whitespace-pre-line leading-snug text-(--color-sand-900)">{data.bio}</p>
          ) : null}

          {data.modalities.length > 0 ? (
            <section>
              <h2 className="mb-2 text-sm font-semibold text-(--color-sand-900)">
                {t('profiles:public.modalities')}
              </h2>
              <ul className="flex flex-wrap gap-2">
                {data.modalities.map((value) => (
                  <li
                    key={value}
                    className="rounded-full bg-(--color-sand-100) px-3 py-1 text-sm text-(--color-sand-700)"
                  >
                    {t(`profiles:modality.${value}`)}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {data.service_modes.length > 0 ? (
            <section>
              <h2 className="mb-2 text-sm font-semibold text-(--color-sand-900)">
                {t('profiles:public.serviceModes')}
              </h2>
              <ul className="flex flex-wrap gap-2">
                {data.service_modes.map((value) => (
                  <li
                    key={value}
                    className="rounded-full bg-(--color-sand-100) px-3 py-1 text-sm text-(--color-sand-700)"
                  >
                    {t(`profiles:serviceMode.${value}`)}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {data.registration_text ? (
            <section className="mh-card">
              <p className="text-sm font-semibold text-(--color-sand-900)">
                {t('profiles:public.registration')}
              </p>
              <p className="mt-1 text-(--color-sand-900)" data-numeric>
                {data.registration_text}
              </p>
              {/* A ressalva fica junto do número, não num rodapé: quem lê o
                  número precisa ler a ressalva no mesmo instante. */}
              <p className="mt-2 text-sm text-(--color-sand-700)">
                {t('profiles:public.registrationDisclaimer')}
              </p>
            </section>
          ) : null}

          <footer className="mt-4 border-t border-(--color-sand-200) pt-4">
            <p className="text-sm text-(--color-sand-700)">{t('profiles:public.howToStart')}</p>
            <Link to="/sign-up" className="mh-button mh-button--secondary mt-3 w-full text-center">
              {t('profiles:public.createAccount')}
            </Link>
          </footer>
        </article>
      ) : null}
    </main>
  );
}
